import * as _ from "lodash-es";
import React, { Component } from "react";
import PaginationDirectionEnum from "../../../models/PaginationDirectionEnum";
import "./PaginationBar.scss";
import OwInputWrapper from "../OwInputWrapper/OwInputWrapper";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import { PaginationBarProps } from "./PaginationBarProps";
import { PaginationBarState } from "./PaginationBarState";

type Props = PaginationBarProps;

type State = PaginationBarState;

export class PaginationBar extends Component<Props, State> {
    readonly state: State = {
        currentLimit: this.props.limit || 25,
        currentOffset: this.props.offset || 0,
        offsetArr: [],
        limitArr: [],
    };

    componentDidMount() {
        const offsetArr = this.createOffsetArr();
        const limitArr = this.createLimitArr();
        const totalPageCount = this.getTotalPageCount();

        this.setState({
            offsetArr,
            limitArr,
            totalPageCount,
        });
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        const offset = this.props.offset;
        const limit = this.props.limit;

        if (offset !== prevProps.offset) {
            this.setState(
                {
                    currentLimit: limit!,
                    currentOffset: offset!,
                },
                () => {
                    const totalPageCount = this.getTotalPageCount();

                    this.setState({
                        totalPageCount,
                    });
                }
            );
        }

        if (prevProps.totalRecords !== this.props.totalRecords) {
            const totalPageCount = this.getTotalPageCount();

            this.setState({
                totalPageCount,
            });
        }
    }

    onPaginationChangedDebounced = () => {
        _.debounce((e: any) => {
            this.onPaginationChanged();
        }, 1000);
    };

    createOffsetArr = () => {
        const offset = this.state.currentOffset;
        const limit = this.state.currentLimit;
        const totalRecords = this.props.totalRecords;

        const startIndex = 1;
        const endIndex = Math.ceil(totalRecords / limit);
        const currentOffset = offset;

        const offsetArr: { value: number; isSelected: boolean }[] = [];

        for (let i = startIndex; i <= endIndex; ++i) {
            const detail = {
                value: i,
                isSelected: false,
            };

            if (detail.value === currentOffset + 1) {
                detail.isSelected = true;
            }

            offsetArr.push(detail);
        }

        return offsetArr;
    };

    getTotalPageCount = () => {
        const limit = this.state.currentLimit;
        const totalRecords = this.props.totalRecords;

        return Math.ceil(totalRecords / limit);
    };

    createLimitArr = () => {
        let limitArr = [
            { value: 5, isSelected: false },
            { value: 10, isSelected: false },
            { value: 25, isSelected: false },
            { value: 50, isSelected: false },
            { value: 100, isSelected: false },
            { value: 250, isSelected: false },
            { value: 500, isSelected: false },
        ];

        if (this.props.limitArr) {
            limitArr = this.props.limitArr;
        }

        const currentLimit = _.get(this.state, "currentLimit", 5);

        for (let i = 0; i < limitArr.length; ++i) {
            const limit = limitArr[i];

            if (limit.value === currentLimit) {
                limitArr[i].isSelected = true;
            }
        }

        return limitArr;
    };

    setOffset = (selectedOffset: number) => {
        this.setState(
            {
                currentOffset: selectedOffset - 1,
            },
            () => {
                const offsetArr = this.createOffsetArr();
                const totalPageCount = this.getTotalPageCount();

                this.setState(
                    {
                        offsetArr,
                        totalPageCount,
                    },
                    () => {
                        this.onPaginationChanged();
                    }
                );
            }
        );
    };

    setLimit = (selectedLimit: number) => {
        this.setState(
            {
                currentLimit: selectedLimit,
                currentOffset: 0,
            },
            () => {
                const limitArr = this.createLimitArr();
                const offsetArr = this.createOffsetArr();
                const totalPageCount = this.getTotalPageCount();

                this.setState(
                    {
                        limitArr,
                        offsetArr,
                        totalPageCount,
                    },
                    () => {
                        this.onPaginationChanged();
                    }
                );
            }
        );
    };

    onPaginationChanged = () => {
        const offset = this.state.currentOffset;
        const limit = this.state.currentLimit;

        this.props.onPaginationChanged(offset, limit);
    };

    offsetSetup = () => {
        const offsetSetup = _.map(this.state.offsetArr, (offset, index) => {
            return (
                <a
                    className={"me-5 pointer text-primary " + (offset.isSelected ? "font-weight-bold offset-selected" : "")}
                    key={index}
                    onClick={() => {
                        this.setOffset(offset.value);
                    }}>
                    {offset.value}
                </a>
            );
        });

        return offsetSetup;
    };

    enterFn = (e: any) => {
        if (e.key === "Enter") {
            this.onPaginationChanged();
        }
    };

    handleOffsetChange = (e: any) => {
        const value = e.target.value;

        const totalPageCount = this.getTotalPageCount();

        if (value > 0 && value <= totalPageCount) {
            this.setState(
                {
                    currentOffset: value - 1,
                },
                () => {
                    this.onPaginationChangedDebounced();
                }
            );
        }
    };

    movePagination = (direction: PaginationDirectionEnum) => {
        let offset = this.state.currentOffset;

        switch (direction) {
            case PaginationDirectionEnum.DECR:
                offset = offset - 1;
                break;

            case PaginationDirectionEnum.INCR:
                offset = offset + 1;
                break;

            case PaginationDirectionEnum.START:
                offset = 0;
                break;

            case PaginationDirectionEnum.END:
                offset = this.state.totalPageCount!;
                break;
        }

        if (offset <= 0) {
            offset = 0;
        }

        if (offset >= this.state.totalPageCount!) {
            offset = this.state.totalPageCount! - 1;
        }

        this.setState(
            {
                currentOffset: offset,
            },
            () => {
                this.onPaginationChanged();
            }
        );
    };

    render() {
        // const offsetDOM = this.offsetSetup();

        const currentPage = this.state.currentOffset + 1;

        const totalPageCount = this.getTotalPageCount();

        let disableDecrJump = this.state.currentOffset === 0 ? true : false;
        let disableIncrJump = this.state.currentOffset === this.state.totalPageCount! - 1 ? true : false;

        if (this.props.isActive) {
            disableDecrJump = true;
            disableIncrJump = true;
        }

        let limitCss = "col-12 col-md-1 d-inline-flex d-print-none mb-2 mb-md-0";

        if (this.props.limitCss) {
            limitCss = this.props.limitCss;
        }

        return (
            <div data-testid={"pagination-bar"} className="PaginationBar mb-3 position-relative">
                <div className="flex-column w-100 flex-wrap justify-content-center align-items-center">
                    {this.props.hideLimit ? null : (
                        <div className={limitCss} style={{ zIndex: 1 }}>
                            <OwSelectOption
                                isActive={this.props.isActive}
                                isDisabledOnActive={true}
                                valueProp={"value"}
                                name={"limit"}
                                titleProp={"value"}
                                selectedOption={this.state}
                                selectedOptionProp={"currentLimit"}
                                options={this.state.limitArr}
                                fn={(e: any) => {
                                    this.setLimit(Number(e.target.value));
                                }}
                            />
                        </div>
                    )}
                    <div
                        className={
                            "col-12 col-md-11 justify-content-between " +
                            (this.props.centered ? "justify-content-md-center" : "justify-content-md-end") +
                            " d-inline-flex pagination-wrapper"
                        }>
                        <div
                            data-testid={"pagination-range"}
                            className={
                                "d-flex justify-content-between " +
                                (this.props.centered ? "justify-content-md-center" : "justify-content-md-end") +
                                " align-items-center flex-grow-1 flex-grow-md-0"
                            }>
                            <div
                                data-testid={"pagination-start"}
                                className={"me-md-4 pagination-left d-print-none " + (disableDecrJump ? "disabled" : "")}
                                onClick={() => {
                                    this.movePagination(PaginationDirectionEnum.START);
                                }}>
                                <i className={"fas fa-fast-backward"} />
                            </div>
                            <div
                                data-testid={"pagination-prev"}
                                className={"me-md-4 pagination-left d-print-none " + (disableDecrJump ? "disabled" : "")}
                                onClick={() => {
                                    this.movePagination(PaginationDirectionEnum.DECR);
                                }}>
                                <i className={"fas fa-play fa-rotate-180 fa-xs"} />
                            </div>
                            <div className={"me-md-4 w-4r"}>
                                <div>
                                    <OwInputWrapper
                                        disabled={this.props.isActive}
                                        name={"offset"}
                                        type={"number"}
                                        className={"text-center w-3r"}
                                        min={1}
                                        max={totalPageCount}
                                        fn={this.handleOffsetChange}
                                        value={currentPage}
                                        onKeyDownFn={this.enterFn}
                                    />
                                </div>
                            </div>
                            {/*{offsetDOM}*/}
                            <div className={"me-md-4"}>of</div>
                            <div className={"me-md-4"}>{totalPageCount}</div>

                            <div
                                data-testid={"pagination-next"}
                                className={"me-md-4 pagination-right d-print-none " + (disableIncrJump ? "disabled" : "")}
                                onClick={() => {
                                    this.movePagination(PaginationDirectionEnum.INCR);
                                }}>
                                <i className={"fas fa-play fa-xs"} />
                            </div>
                            <div
                                data-testid={"pagination-end"}
                                className={"pagination-right d-print-none " + (disableIncrJump ? "disabled" : "")}
                                onClick={() => {
                                    this.movePagination(PaginationDirectionEnum.END);
                                }}>
                                <i className={"fas fa-fast-forward"} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default PaginationBar;
